<template>
  <v-dialog max-width="640px" v-model="showDialog" persistent>
    <v-card>
      <v-card-title class="secondary white--text py-3 px-5">
        <v-icon color="white" class="mr-2">mdi-file-plus-outline</v-icon>
        <span class="text-subtitle-1 font-weight-bold">Create DA (Sub-hire)</span>
        <v-spacer />
        <v-btn icon color="white" small @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="px-6 pt-5 pb-2">
        <v-row dense>
          <v-col cols="6">
            <MultiSelect
              label="Origin *"
              :items="locationList"
              itemText="location_code"
              itemValue="id"
              outlined
              dense
              clearable
              hide-details="auto"
              v-model="form.origin"
            />
          </v-col>
          <v-col cols="6">
            <MultiSelect
              label="Destination *"
              :items="locationList"
              itemText="location_code"
              itemValue="id"
              outlined
              dense
              clearable
              hide-details="auto"
              v-model="form.destination"
            />
          </v-col>
          <v-col cols="6">
            <MultiSelect
              label="Container Type *"
              :items="containerTypeList"
              itemText="type_name"
              itemValue="id"
              outlined
              dense
              clearable
              hide-details="auto"
              v-model="form.container_type"
            />
          </v-col>
          <v-col cols="6">
            <v-select
              label="Job Type *"
              :items="jobTypes"
              outlined
              dense
              hide-details="auto"
              v-model="form.job_type"
            />
          </v-col>
          <v-col cols="12">
            <div v-if="container.vendor_name">
              <div class="text-caption grey--text">Vendor</div>
              <div class="text-body-2 font-weight-medium">{{ container.vendor_name }}</div>
            </div>
            <MultiSelect
              v-else
              label="Vendor *"
              :items="vendorList"
              itemText="company_title"
              itemValue="id"
              :loading="vendorLoading"
              outlined
              dense
              clearable
              hide-details="auto"
              v-model="form.vendor"
              @change="fetchRatePreview"
            />
          </v-col>
        </v-row>

        <v-divider class="my-3" />

        <div class="d-flex align-center mb-1">
          <span class="text-caption grey--text mr-2" style="min-width: 120px">Resolved DA Rate</span>
          <v-progress-circular v-if="rateLoading" indeterminate size="16" width="2" color="primary" />
          <span v-else-if="preview.da_rate != null" class="text-subtitle-2 font-weight-bold">
            AED {{ preview.da_rate }}
          </span>
          <span v-else class="text-caption grey--text">—</span>
        </div>
        <div class="d-flex align-center">
          <span class="text-caption grey--text mr-2" style="min-width: 120px">Sub-hire Rate</span>
          <span v-if="!rateLoading && preview.subhire_rate != null" class="text-subtitle-2 font-weight-bold">
            AED {{ preview.subhire_rate }}
          </span>
          <span v-else-if="!rateLoading" class="text-caption warning--text">
            {{ preview.detail || "Select route, container type, job type and vendor" }}
          </span>
        </div>

        <p class="text-caption grey--text mt-3 mb-0">
          The vendor's tariff rate is set as the sub-hire cost on this container - the
          DA/Leg won't be created if no rate can be resolved. This does not create a
          Sub-hire Job; that still happens when the vendor's file is uploaded and approved.
        </p>
      </v-card-text>

      <v-divider class="mt-3" />

      <v-card-actions class="px-5 py-3">
        <v-spacer />
        <v-btn depressed outlined @click="closeDialog" class="mr-2">Cancel</v-btn>
        <v-btn
          color="primary"
          depressed
          :loading="submitting"
          :disabled="!isFormValid"
          min-width="120"
          @click="submit"
        >
          <v-icon small class="mr-1">mdi-check-circle</v-icon>
          Confirm
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";

const JOB_TYPES = ["Import", "Export", "FCL", "LCL", "Empty"];

export default {
  name: "CreateSubhireContainerDADialog",
  components: { MultiSelect },
  props: {
    value: { type: Boolean, default: false },
    container: { type: Object, default: () => ({}) },
  },
  watch: {
    value(val) {
      if (val) {
        this.resetForm();
        this.fetchInitialData();
        this.fetchRatePreview();
      }
    },
    "form.origin": "fetchRatePreview",
    "form.destination": "fetchRatePreview",
    "form.container_type": "fetchRatePreview",
    "form.job_type": "fetchRatePreview",
  },
  data() {
    return {
      jobTypes: JOB_TYPES,
      form: {
        origin: null,
        destination: null,
        container_type: null,
        job_type: "Import",
        vendor: null,
      },
      locationList: [],
      containerTypeList: [],
      vendorList: [],
      vendorLoading: false,
      submitting: false,
      rateLoading: false,
      preview: { da_rate: null, subhire_rate: null, detail: null },
      _rateTimer: null,
    };
  },
  computed: {
    showDialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    isFormValid() {
      return (
        !!this.form.origin &&
        !!this.form.destination &&
        !!this.form.container_type &&
        !!this.form.job_type &&
        (!!this.container.vendor_name || !!this.form.vendor) &&
        this.preview.da_rate != null &&
        this.preview.subhire_rate != null
      );
    },
  },
  methods: {
    getVal(v) {
      return v && typeof v === "object" ? v.id : v;
    },
    resetForm() {
      this.form = {
        origin: this.container.origin || null,
        destination: this.container.destination || null,
        container_type: this.container.container_type || null,
        job_type: this.container.job_type || "Import",
        vendor: null,
      };
      this.preview = { da_rate: null, subhire_rate: null, detail: null };
    },
    closeDialog() {
      this.showDialog = false;
    },
    fetchInitialData() {
      this.$api.location.getLocationSelectList({ limit: "all" }).then((res) => {
        this.locationList = res.data;
      });
      this.$api.containerType.getContainerTypeList({ limit: "all" }).then((res) => {
        this.containerTypeList = res.data;
      });
      if (!this.container.vendor_name) {
        this.fetchVendors();
      }
    },
    fetchRatePreview() {
      clearTimeout(this._rateTimer);
      const { origin, destination, container_type, job_type, vendor } = this.form;
      if (!origin || !destination || !container_type || !job_type) {
        this.preview = { da_rate: null, subhire_rate: null, detail: null };
        return;
      }
      this._rateTimer = setTimeout(() => {
        this.rateLoading = true;
        const params = {
          origin: this.getVal(origin),
          destination: this.getVal(destination),
          container_type: this.getVal(container_type),
          job_type,
        };
        if (!this.container.vendor_name && vendor) {
          params.vendor = this.getVal(vendor);
        }
        this.$api.emergencyOrderContainer
          .getDaRatePreview(this.container.id, params)
          .then((res) => {
            const d = res.data || res;
            this.preview = {
              da_rate: d.da_rate,
              subhire_rate: d.subhire_rate,
              detail: d.detail,
            };
          })
          .catch((err) => {
            this.preview = {
              da_rate: null,
              subhire_rate: null,
              detail: err?.message || "Couldn't resolve rate",
            };
          })
          .finally(() => {
            this.rateLoading = false;
          });
      }, 300);
    },
    fetchVendors() {
      this.vendorLoading = true;
      this.$api.company
        .getCompanySelectList({ company_type: "Transporter", limit: 500 })
        .then((res) => {
          this.vendorList = res.data;
        })
        .catch(() => {})
        .finally(() => {
          this.vendorLoading = false;
        });
    },
    submit() {
      this.submitting = true;
      const payload = {
        origin: this.getVal(this.form.origin),
        destination: this.getVal(this.form.destination),
        container_type: this.getVal(this.form.container_type),
        job_type: this.form.job_type,
      };
      if (!this.container.vendor_name) {
        payload.vendor = this.getVal(this.form.vendor);
      }
      this.$api.emergencyOrderContainer
        .createManualDA(this.container.id, payload)
        .then(() => {
          this.$bus.$emit("showToastMessage", {
            message: "DA created successfully!",
            color: "success",
          });
          this.$emit("daCreated");
          this.closeDialog();
        })
        .catch((err) => {
          this.$bus.$emit("showToastMessage", {
            message: err?.message || "Failed to create DA.",
            color: "error",
          });
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>
